import { motion } from "framer-motion";
import { Check, ArrowUpRight, Droplets, Bike, Info } from "lucide-react";
import { FORMULES, VEHICLE_TYPES, MOTO, OPTIONS, OFFERED } from "@/data";

const Column = ({ title, items }) => (
  <div>
    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[var(--blue)]">{title}</span>
    <ul className="mt-3 space-y-2">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2 text-[13px] text-[var(--txt-1)] leading-snug">
          <Check size={13} className="mt-[3px] shrink-0 text-[var(--blue)]" />
          {it}
        </li>
      ))}
    </ul>
  </div>
);

export const Services = ({ onSelect }) => {
  return (
    <section id="formules" data-testid="services-pricing-section" className="relative bg-[var(--bg-0)] py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[var(--blue)]">[ Nos formules ]</span>
            <h2 className="mt-4 font-display font-bold uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">
              Choisissez votre
              <br />
              formule
            </h2>
          </div>
          <p className="max-w-sm text-[var(--txt-1)] text-base leading-relaxed">
            Trois niveaux de prestation, tarifés selon votre type de véhicule.
            Intérieur et extérieur, du lavage soigné au detailing complet.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {FORMULES.map((f, i) => (
            <motion.div
              key={f.id}
              data-testid={`formule-card-${f.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative flex flex-col rounded-md border bg-[var(--bg-1)] p-7 transition-colors duration-300 ${
                f.popular ? "border-[var(--blue)]/60" : "border-white/10 hover:border-white/30"
              }`}
            >
              {f.popular && (
                <span className="absolute -top-3 left-7 bg-[var(--blue)] text-white font-mono2 text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-sm">
                  Le plus demandé
                </span>
              )}
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold uppercase text-2xl tracking-tight" style={{ color: f.accent }}>
                  {f.name.replace("Formule ", "")}
                </h3>
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Formule</span>
              </div>

              {/* Price table by vehicle type */}
              <div className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-white/10 bg-white/10">
                {VEHICLE_TYPES.map((v) => (
                  <div key={v.id} className="bg-[var(--bg-2)] px-3 py-2.5 flex items-center justify-between">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[var(--txt-1)]">{v.label}</span>
                    <span className="font-mono2 text-sm font-bold text-white">{f.prices[v.id]}€</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-5">
                <Column title="Extérieur" items={f.exterieur} />
                <Column title="Intérieur" items={f.interieur} />
              </div>

              <button
                data-testid={`formule-select-${f.id}`}
                onClick={() => onSelect(f.apiKey)}
                className={`group/btn mt-7 inline-flex items-center justify-between rounded-sm px-4 py-3 font-mono2 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  f.popular
                    ? "bg-[var(--blue)] text-white hover:bg-[var(--blue-hi)]"
                    : "bg-white text-black hover:bg-[var(--blue)] hover:text-white"
                }`}
              >
                Réserver cette formule
                <ArrowUpRight size={15} className="hover-arrow" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Moto + options */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="moto-card"
            className="flex items-center justify-between rounded-md border border-white/10 bg-[var(--bg-1)] p-7"
          >
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-[var(--blue)]/10 text-[var(--blue)]">
                <Bike size={22} />
              </span>
              <div>
                <h4 className="font-display font-semibold text-lg">Lavage spécial moto</h4>
                <p className="text-sm text-[var(--txt-2)]">Deux-roues chouchoutés</p>
              </div>
            </div>
            <div className="text-right">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[var(--txt-2)]">dès</span>
              <div className="font-mono2 text-3xl font-bold text-white">{MOTO.price}€</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08 }}
            data-testid="options-card"
            className="lg:col-span-2 rounded-md border border-white/10 bg-[var(--bg-1)] p-7"
          >
            <div className="flex items-center gap-2 mb-4">
              <Droplets size={16} className="text-[var(--blue)]" />
              <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-white">Options à la carte</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {OPTIONS.map((o) => (
                <div key={o.label} className="flex items-center justify-between border-b border-white/5 pb-2">
                  <span className="text-sm text-[var(--txt-1)]">{o.label}</span>
                  <span className="font-mono2 text-sm font-bold text-white">{o.price}€</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <p className="mt-8 flex items-start gap-2 font-mono2 text-[11px] uppercase tracking-[0.12em] text-[var(--txt-2)]">
          <Info size={14} className="mt-[1px] shrink-0 text-[var(--blue)]" />
          {OFFERED}
        </p>
      </div>
    </section>
  );
};
